import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, CircularProgress, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Link } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { notificationApi } from '../../services/notificationApi';
import { noticeApi } from '../../services/noticeApi';

const noticeReadKey = (userID) => `ja-legends-notice-reads:${userID}`;
const getReadNoticeIDs = (userID) => {
  try { return new Set(JSON.parse(window.localStorage.getItem(noticeReadKey(userID)) || '[]')); }
  catch { return new Set(); }
};
const saveReadNoticeIDs = (userID, ids) => {
  try { window.localStorage.setItem(noticeReadKey(userID), JSON.stringify([...ids])); }
  catch { /* Browsing can continue when local storage is unavailable. */ }
};
const noticeDismissedKey = (userID) => `ja-legends-notice-dismissed:${userID}`;
const getDismissedNoticeIDs = (userID) => {
  try { return new Set(JSON.parse(window.localStorage.getItem(noticeDismissedKey(userID)) || '[]')); }
  catch { return new Set(); }
};
const saveDismissedNoticeIDs = (userID, ids) => {
  try { window.localStorage.setItem(noticeDismissedKey(userID), JSON.stringify([...ids])); }
  catch { /* Browsing can continue when local storage is unavailable. */ }
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
};

export default function NotificationCenter() {
  const { user } = useAuthenticator((context) => [context.user]);
  const userID = user?.attributes?.sub;
  const [anchor, setAnchor] = useState(null);
  const [state, setState] = useState({ loading: true, items: [], available: true });

  const load = useCallback(async () => {
    if (!userID) return;
    const [personalResult, noticeResult] = await Promise.allSettled([
      notificationApi.listByUser(userID),
      noticeApi.listActiveFor('NOTIFICATION'),
    ]);
    const personalItems = personalResult.status === 'fulfilled' ? personalResult.value : [];
    const readNoticeIDs = getReadNoticeIDs(userID);
    const dismissedNoticeIDs = getDismissedNoticeIDs(userID);
    const noticeItems = noticeResult.status === 'fulfilled' ? noticeResult.value.filter((notice) => !dismissedNoticeIDs.has(notice.id)).map((notice) => ({
      id: `notice-${notice.id}`,
      noticeID: notice.id,
      type: 'NOTICE',
      title: notice.title,
      body: notice.content || (notice.date ? 'A new JA Legends activity has been announced.' : 'New announcement from JA Legends.'),
      link: '/dashboard#notices',
      isRead: readNoticeIDs.has(notice.id),
      isNotice: true,
      isImportant: Boolean(notice.isImportant),
      createdAt: notice.createdAt,
    })) : [];
    const items = [...noticeItems, ...personalItems].sort((a, b) => {
      if (Boolean(a.isImportant) !== Boolean(b.isImportant)) return a.isImportant ? -1 : 1;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    setState({ loading: false, items, available: personalResult.status === 'fulfilled' || noticeResult.status === 'fulfilled' });
  }, [userID]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!userID) return undefined;

    const refresh = () => load();
    const stopWatching = noticeApi.watch(
      refresh,
      (error) => console.info('Live notice updates are temporarily unavailable.', error),
    );

    // Refreshing on focus and periodically is a fallback for interrupted mobile
    // connections and ensures messages sent while a member is away still appear.
    window.addEventListener('focus', refresh);
    const intervalID = window.setInterval(refresh, 60000);

    return () => {
      stopWatching();
      window.removeEventListener('focus', refresh);
      window.clearInterval(intervalID);
    };
  }, [load, userID]);
  const unread = useMemo(() => state.items.filter((item) => !item.isRead).length, [state.items]);

  const markAllRead = async () => {
    const unreadItems = state.items.filter((item) => !item.isRead);
    setState((current) => ({ ...current, items: current.items.map((item) => ({ ...item, isRead: true })) }));
    const noticeIDs = unreadItems.filter((item) => item.isNotice).map((item) => item.noticeID);
    if (noticeIDs.length) {
      const readIDs = getReadNoticeIDs(userID);
      noticeIDs.forEach((id) => readIDs.add(id));
      saveReadNoticeIDs(userID, readIDs);
    }
    await Promise.allSettled(unreadItems.filter((item) => !item.isNotice).map((item) => notificationApi.markRead(item.id)));
  };

  const openNotification = (item) => {
    setAnchor(null);
    if (!item.isRead) {
      setState((current) => ({ ...current, items: current.items.map((entry) => entry.id === item.id ? { ...entry, isRead: true } : entry) }));
      if (item.isNotice) {
        const readIDs = getReadNoticeIDs(userID);
        readIDs.add(item.noticeID);
        saveReadNoticeIDs(userID, readIDs);
      } else {
        notificationApi.markRead(item.id).catch(() => {});
      }
    }
  };

  const clearItem = async (item) => {
    setState((current) => ({ ...current, items: current.items.filter((entry) => entry.id !== item.id) }));
    if (item.isNotice) {
      const dismissedIDs = getDismissedNoticeIDs(userID);
      dismissedIDs.add(item.noticeID);
      saveDismissedNoticeIDs(userID, dismissedIDs);
      return;
    }
    await notificationApi.remove(item.id).catch((error) => console.info('Unable to remove notification.', error));
  };

  const clearAll = async () => {
    const items = state.items;
    setState((current) => ({ ...current, items: [] }));
    const dismissedIDs = getDismissedNoticeIDs(userID);
    items.filter((item) => item.isNotice).forEach((item) => dismissedIDs.add(item.noticeID));
    saveDismissedNoticeIDs(userID, dismissedIDs);
    await Promise.allSettled(items.filter((item) => !item.isNotice).map((item) => notificationApi.remove(item.id)));
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton className="dance-nav-notifications" aria-label={`${unread} unread notifications`} onClick={(event) => setAnchor(event.currentTarget)}>
          <Badge badgeContent={unread} color="error" max={9}><NotificationsNoneIcon /></Badge>
        </IconButton>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ className: 'notification-popover' }}>
        <Box className="notification-heading">
          <Box><Typography variant="overline">Your activity</Typography><Typography variant="h6" fontWeight={950}>Notifications</Typography></Box>
          <Stack direction="row" spacing={0.5}>
            {unread > 0 && <Button size="small" onClick={markAllRead}>Mark read</Button>}
            {!!state.items.length && <Button size="small" color="inherit" onClick={clearAll}>Clear all</Button>}
          </Stack>
        </Box>
        {state.loading ? <Box className="notification-state"><CircularProgress size={28} /></Box> : !state.available ? <Box className="notification-state"><Typography fontWeight={850}>Notifications are being prepared.</Typography></Box> : !state.items.length ? <Box className="notification-state"><CheckCircleOutlineIcon color="success" /><Typography fontWeight={850}>You’re all caught up!</Typography></Box> : (
          <Stack className="notification-list">
            {state.items.slice(0, 12).map((item) => (
              <Box className={`notification-row ${item.isRead ? '' : 'is-unread'} ${item.isImportant ? 'is-important' : ''}`} key={item.id}>
                <Box component={item.link ? Link : 'button'} to={item.link} className="notification-row-main" onClick={() => openNotification(item)}>
                  <span className="notification-dot" />
                  <span><strong>{item.isImportant ? 'Important · ' : ''}{item.title}</strong><small>{item.body}</small><em>{item.isNotice ? `Notice · ${formatTime(item.createdAt)}` : formatTime(item.createdAt)}</em></span>
                </Box>
                <Tooltip title="Clear notification" arrow>
                  <IconButton size="small" className="notification-clear" aria-label={`Clear ${item.title}`} onClick={() => clearItem(item)}><CloseIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </Popover>
    </>
  );
}
